/** @format */

import { RolesPayload } from "./rolesType";
import { IUnion, IUnionPayload, MemberCircle } from "./unionType";
import { RatingForUsers } from "./userRatingType";

export interface IUser {
	_id?: string;
	firstName: string;
	middleName?: string | null;
	lastName: string;
	profileImage?: string | null;
	email: string;
	dateOfBirth?: string | null;
	password: string;
	socialSecurityNumber?: string | null;
	phone?: IPhoneContact | null;
	address?: IAddress | null;
	employer?: IEmployer | null;
	unions?: IUnionPayload | null;
	unionLeaderOf?: IUnionPayload | null;
	roles?: RolesPayload[] | null;
	hasAgreedToTerms: boolean;
	pendingAddRequests: IAddRequestPayload[] | null;
	isVerified?: boolean;
	hasSeenOnboarding?: boolean; // Tracks if user has seen the "How It Works" modal
	cognitoId?: string;
	stripeCustomerId?: string;
}

export interface IPhoneContact {
	countryCode?: string | null;
	number: string | null;
	e164?: string | null;
	country?: string | null;
	verifiedAt?: string | null;
	consent?: {
		status: "opted_in" | "opted_out" | "unknown";
		source?: "signup_form" | "sms_keyword" | "admin" | "import" | "other";
		timeStamp?: string | null;
	};
	optedOutAt?: string | null;
	lastUsedAt?: string | null;
}

export const EmptyPhoneContact: IPhoneContact = {
	countryCode: "",
	number: "",
	e164: "",
	country: "",
	verifiedAt: null,
	consent: {
		status: "unknown",
		source: undefined,
		timeStamp: null,
	},
	optedOutAt: null,
	lastUsedAt: null,
};

export interface IAddress {
	streetName: string | null;
	zipCode: string | null;
	city: string | null;
	state?: string | null;
	county?: string | null;
	region?: string | null;
	country?: string | null;
}

export const EmptyAddress: IAddress = {
	streetName: "",
	zipCode: "",
	city: "",
	state: "",
	country: "",
	county: "",
};

export interface IEmployer {
	employerName?: string | null;
	yearsWithEmployer?: number | null;
	income?: number | null;
	streetName: string | null;
	zipCode: string | null;
	city: string | null;
	state?: string | null;
	county?: string | null;
	region?: string | null;
	country?: string | null;
}

export const EmptyEmployer: IEmployer = {
	employerName: "",
	yearsWithEmployer: 0,
	income: 0,
	streetName: "",
	zipCode: "",
	city: "",
	state: "",
	country: "",
	county: "",
};

export enum AddRequestStatus {
	Pending = "Pending",
	Approved = "Approved",
	Rejected = "Rejected",
}

export interface IAddRequestPayload {
	_id: string;
	member: string;
	union: IUnion;
	status: AddRequestStatus;
	created_At?: string | Date;
	updated_At?: string | Date;
}

export interface IUserPayload {
	_id?: string;
	firstName: string;
	middleName?: string | null;
	lastName: string;
	profileImage?: string | null;
	email: string;
	dateOfBirth?: string | null;
	password: string;
	socialSecurityNumber?: string | null;
	phone?: IPhoneContact | null;
	address?: IAddress | null;
	employer?: IEmployer | null;
	unions?: MemberCircle[] | null;
	unionLeaderOf?: IUnionPayload[] | null;
	roles?: RolesPayload[] | null;
	hasAgreedToTerms: boolean;
	pendingAddRequests: IAddRequestPayload[] | null;
	stripeCustomerId?: string | null;
	stripeAccountId?: string | null;
	isVerified?: boolean;
	isBanned?: boolean;
	bannedAt?: Date;
	banReason?: string;
	hasSeenOnboarding?: boolean;
}

export interface ISearchUserSendAddRequestPayload {
	_id: string;
	firstName: string;
	lastName: string;
	email: string;
	phone: IPhoneContact | null;
}

export interface IUserUpdatePayload {
	firstName: string;
	middleName?: string | null;
	lastName: string;
	profileImage?: string | null;
	email: string;
	phone?: IPhoneContact | null;
}

export interface IUserAccessPayload {
	accessToken: string;
	idToken: string;
}

export interface IPasswordResetPayload {
	email: string;
}

export interface IConfirmPasswordResetPayload {
	email: string;
	code: string;
	newPassword: string;
}

export const EmptyPasswordResetPayload = {
	email: "",
};

export const EmptyConfirmPasswordResetPayload = {
	email: "",
	code: "",
	newPassword: "",
};

export const EmptyUserPayload: IUserPayload = {
	_id: "",
	firstName: "",
	middleName: "",
	lastName: "",
	profileImage: "",
	email: "",
	dateOfBirth: "",
	password: "",
	socialSecurityNumber: "",
	phone: EmptyPhoneContact,
	address: EmptyAddress,
	employer: EmptyEmployer,
	unions: null,
	unionLeaderOf: null,
	roles: null,
	hasAgreedToTerms: false,
	pendingAddRequests: null,
	stripeCustomerId: "",
};

export const EmptySearchUserSendAddRequestPayload: ISearchUserSendAddRequestPayload =
	{
		_id: "",
		firstName: "",
		lastName: "",
		email: "",
		phone: EmptyPhoneContact,
	};

export interface CircleMember {
	_id: string;
	memberId: Partial<IUser>;
	hasAgreedToCircleTerms: boolean;
}

export interface CircleLeader {
	_id: string;
	firstName: string;
	lastName: string;
	profileImage: string;
	email: string;
	isVerified?: boolean;
}

export interface CircleContribution {
	_id: string;
	contributionAmount: string;
}

export interface Circle {
	_id: string;
	circleName: string;
	circleImage: string;
	description: string;
	membersCount: string;
	members: CircleMember[];
	invitedMembers: Partial<IUser>[];
	joinRequests: Partial<IUser>[];
	leader: CircleLeader;
	contributions: CircleContribution[];
	payOuts: string[];
	created_At: string;
	updated_At: string;
}

export interface MemberCircles {
	message?: string;
	circles: Circle[];
}

export interface UserRating {
	rating: RatingForUsers;
}

export interface UserJoinRequests {
	_id: string;
	userId: string;
	circleId: {
		_id: string;
		circleName: string;
	};
	reason: string;
	status: string;
	deniedAt: Date;
	created_At: Date;
	updated_At: Date;
}

export interface IUserFinancialSummary {
	userId: string;
	activeCirclesCount: number;
	totalPayOuts: number;
	totalExpectedPayOuts: number;
	totalContributions: number;
}

export interface UserSummary {
	userId: string;
	firstName: string;
	lastName: string;
	email: string;
	profileImage?: string;
	phone?: IPhoneContact | null;
	isVerified: boolean;
	isActive?: boolean;
	isBanned?: boolean;
	isDeleted?: boolean;
	cognitoId: string;
	stripeCustomerId?: string | null;
	stripeAccountId?: string;
	circleCount: number;
	leaderOfCount: number;
	rolesCount: number;
	createdAt?: Date;
	lastLogin?: Date;
	circles?: Array<{
		_id: string;
		circleName: string;
		circleImage?: string;
		isLeader: boolean;
	}>;
}

export interface AllUsersResponse {
	totalUsers: number;
	users: UserSummary[];
	generatedAt: Date;
}
